import React, { useState, useEffect } from 'react';
import { Tooltip, Box, Typography, IconButton, useTheme } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import HelpIcon from '@mui/icons-material/Help';
import { useTranslation } from '../store/languageStore';

interface FieldValidatorProps {
  fieldPath: string;
  value: string;
  showHelperText?: boolean;
}

// BEO ATLAS field regex patterns (subset of common patterns)
const FIELD_PATTERNS: Record<string, { pattern: RegExp; hint: string; example?: string }> = {
  // Dates
  'kopf.datum': {
    pattern: /^[0-9]{4}[0-9]{2}[0-9]{2}$/,
    hint: 'Format: YYYYMMDD',
    example: '20250110'
  },
  'kopf.zeitpunktderAnmeldung': {
    pattern: /^[0-9]{14}$/,
    hint: 'Format: YYYYMMDDHHmmss',
    example: '20250110120000'
  },

  // Country codes
  'kopf.ausfuhrer.land': {
    pattern: /^[A-Z]{2}$/,
    hint: '2-letter country code',
    example: 'DE, US, FR'
  },
  'kopf.empfanger.land': {
    pattern: /^[A-Z]{2}$/,
    hint: '2-letter country code',
    example: 'DE, US, FR'
  },
  'kopf.bestimmungsLand': {
    pattern: /^[A-Z]{2}$/,
    hint: '2-letter country code',
    example: 'DE, US, FR'
  },
  'ursprungsland': {
    pattern: /^[A-Z]{2}$/,
    hint: '2-letter country code',
    example: 'DE, US, FR'
  },

  // Currency
  'kopf.rechnungswahrung': {
    pattern: /^[A-Z]{3}$/,
    hint: '3-letter currency code',
    example: 'EUR, USD, CHF'
  },

  // Street addresses
  'kopf.ausfuhrer.strasse': {
    pattern: /^[A-Za-z0-9\s\-\./,()&]+$/,
    hint: 'Only alphanumeric and -./,()& allowed',
    example: 'Musterstrasse 123'
  },
  'strasse': {
    pattern: /^[A-Za-z0-9\s\-\./,()&]+$/,
    hint: 'Only alphanumeric and -./,()& allowed',
    example: 'Main Street 45'
  },

  // TIN/EORI
  'kopf.ausfuhrer.tin': {
    pattern: /^[A-Z]{2}[\x21-\x7E]{1,15}$/,
    hint: 'Country code + 1-15 characters',
    example: 'DE123456789'
  },
  'kopf.empfanger.tin': {
    pattern: /^[A-Z]{2}[\x21-\x7E]{1,15}$/,
    hint: 'Country code + 1-15 characters',
    example: 'US987654321'
  },

  // HS Codes
  'wareWarennummerKN8': {
    pattern: /^[0-9]{8}$/,
    hint: '8-digit HS/CN code',
    example: '85176200'
  },

  // Customs offices
  'kopf.ausfuhrzollstelleDienststellennummer': {
    pattern: /^DE0[01][0-9]{4}$/,
    hint: 'Format: DE0[01]XXXX',
    example: 'DE002152'
  },
  'kopf.gestellungszollstelle': {
    pattern: /^[A-Z]{2}[A-Za-z0-9]{6}$/,
    hint: 'Country code + 6 alphanumeric',
    example: 'DE002152'
  },

  // Position numbers
  'warePositionsnummer': {
    pattern: /^[1-9][0-9]{0,2}$/,
    hint: '1-999',
    example: '1, 2, 3'
  },

  // Procedures
  'beantragtesVerfahren': {
    pattern: /^[0-9]{2}$/,
    hint: '2-digit code',
    example: '10'
  },
  'vorhergehendesVerfahren': {
    pattern: /^[0-9]{2}$/,
    hint: '2-digit code',
    example: '00'
  },

  // Weights and amounts
  'wareEigenmasse': {
    pattern: /^(?:0|[1-9][0-9]{0,15})(?:[.,][0-9]{1,6})?$/,
    hint: 'Decimal number (kg)',
    example: '12.5, 100.25'
  },
  'wareRohmasse': {
    pattern: /^(?:0|[1-9][0-9]{0,15})(?:[.,][0-9]{1,6})?$/,
    hint: 'Decimal number (kg)',
    example: '13.5, 105.25'
  },
  'kopf.gesamtRohmasse': {
    pattern: /^(?:0|[1-9][0-9]{0,15})(?:[.,][0-9]{1,3})?$/,
    hint: 'Decimal number (kg)',
    example: '250.5'
  },
  'kopf.inRechnunggestellterGesamtbetrag': {
    pattern: /^(?:0|[1-9][0-9]{0,15})(?:[.,][0-9]{1,2})?$/,
    hint: 'Amount with up to 2 decimals',
    example: '15000.00'
  },

  // Email
  'email': {
    pattern: /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/,
    hint: 'Valid email address',
    example: 'info@example.com'
  },

  // Incoterms
  'lieferbedingungIncotermCode': {
    pattern: /^[A-Z]{3}$/,
    hint: '3-letter Incoterm',
    example: 'EXW, FOB, CIF, DAP, DDP'
  },

  // Article types
  'kopf.artderAnmeldung': {
    pattern: /^[A-Z]{2}$/,
    hint: '2-letter code',
    example: 'EX'
  },
  'kopf.artderAusfuhranmeldung': {
    pattern: /^[0-9]{8}$/,
    hint: '8-digit code',
    example: '20000000'
  },
  'kopf.beteiligtenKonstellation': {
    pattern: /^[0-9]{4}$/,
    hint: '4-digit code',
    example: '0001'
  }
};

// Helper function to get pattern for a field
const getFieldPattern = (fieldPath: string) => {
  // Direct match
  if (FIELD_PATTERNS[fieldPath]) {
    return FIELD_PATTERNS[fieldPath];
  }

  // Check if field ends with a known pattern (for array fields)
  for (const [key, value] of Object.entries(FIELD_PATTERNS)) {
    if (fieldPath.includes(key) || fieldPath.endsWith('.' + key.split('.').pop())) {
      return value;
    }
  }

  return null;
};

// Convert regex pattern to user-friendly description
const getPatternDescription = (pattern: { pattern: RegExp; hint: string; example?: string }) => {
  let description = pattern.hint;
  if (pattern.example) {
    description += ` (e.g., ${pattern.example})`;
  }
  return description;
};

const FieldValidator: React.FC<FieldValidatorProps> = ({ fieldPath, value, showHelperText = false }) => {
  const theme = useTheme();
  const { tString } = useTranslation();
  const [validationState, setValidationState] = useState<'valid' | 'invalid' | 'warning' | 'empty'>('empty');
  const [validationMessage, setValidationMessage] = useState<string>('');

  useEffect(() => {
    if (!value || value === '') {
      setValidationState('empty');
      setValidationMessage('');
      return;
    }

    const pattern = getFieldPattern(fieldPath);
    if (!pattern) {
      // No pattern defined for this field
      setValidationState('warning');
      setValidationMessage(tString('fieldValidator.noPattern'));
      return;
    }

    // Test the value against the pattern
    const isValid = pattern.pattern.test(value);

    if (isValid) {
      setValidationState('valid');
      setValidationMessage(tString('fieldValidator.validFormat'));
    } else {
      setValidationState('invalid');
      setValidationMessage(getPatternDescription(pattern));
    }
  }, [fieldPath, value, tString]);

  // Icon to show based on validation state
  const getValidationIcon = () => {
    switch (validationState) {
      case 'valid':
        return <CheckCircleIcon sx={{ fontSize: 20, color: theme.palette.success.main }} />;
      case 'invalid':
        return <ErrorIcon sx={{ fontSize: 20, color: theme.palette.error.main }} />;
      case 'warning':
        return <WarningIcon sx={{ fontSize: 20, color: theme.palette.warning.main }} />;
      case 'empty':
        return null;
    }
  };

  // Get the pattern info for display
  const pattern = getFieldPattern(fieldPath);
  const formatHint = pattern ? getPatternDescription(pattern) : null;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      {/* Validation status icon */}
      {validationState !== 'empty' && (
        <Tooltip title={validationMessage}>
          <Box>{getValidationIcon()}</Box>
        </Tooltip>
      )}

      {/* Help icon with format hint */}
      {formatHint && (
        <Tooltip
          title={
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                {tString('fieldValidator.expectedFormat')}:
              </Typography>
              <Typography variant="body2">
                {formatHint}
              </Typography>
            </Box>
          }
        >
          <HelpIcon sx={{ fontSize: 18, color: 'text.secondary', cursor: 'help' }} />
        </Tooltip>
      )}

      {/* Helper text below field (optional) */}
      {showHelperText && formatHint && validationState === 'empty' && (
        <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
          {pattern?.hint}
        </Typography>
      )}
    </Box>
  );
};

export default FieldValidator;

// Export validation function for external use
export const validateFieldValue = (fieldPath: string, value: string): { isValid: boolean; message: string } => {
  if (!value || value === '') {
    return { isValid: false, message: 'Field is empty' };
  }

  const pattern = getFieldPattern(fieldPath);
  if (!pattern) {
    return { isValid: true, message: 'No validation pattern defined' };
  }

  const isValid = pattern.pattern.test(value);
  return {
    isValid,
    message: isValid ? 'Valid format' : getPatternDescription(pattern)
  };
};